import { Link } from 'react-router-dom';
import { Clock, ChefHat, Star, Sparkles } from 'lucide-react';
import { Badge } from '../ui/badge';
import { motion } from 'framer-motion';

const MEAL_TYPE_LABELS = {
  entree: 'Entrée',
  plat: 'Plat',
  dessert: 'Dessert',
};

const MEAL_TYPE_COLORS = {
  entree: 'bg-worldplate-secondary',
  plat: 'bg-worldplate-primary',
  dessert: 'bg-worldplate-accent text-worldplate-text',
};

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1699572583203-97170ae281c0?w=400&q=80',
  'https://images.unsplash.com/photo-1673553518378-34d8186e57b6?w=400&q=80',
  'https://images.unsplash.com/photo-1711633648854-50a30a6df74d?w=400&q=80',
  'https://images.unsplash.com/photo-1642346854073-a88b5a77038c?w=400&q=80',
  'https://images.unsplash.com/photo-1650539062879-1ec3b24f1bde?w=400&q=80',
];

export const RecipeCard = ({ recipe, index = 0 }) => {
  const imageUrl = recipe.image_url || PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
  const totalTime = recipe.prep_time + recipe.cook_time;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
    >
      <Link
        to={`/recipes/${recipe.id}`}
        className="block group"
        data-testid={`recipe-card-${recipe.id}`}
      >
        <article className="bg-white rounded-2xl overflow-hidden border border-stone-100 shadow-sm hover:shadow-xl transition-shadow recipe-card">
          {/* Image */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={imageUrl}
              alt={recipe.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            
            {/* Badges */}
            <div className="absolute top-3 left-3 flex gap-2">
              <Badge className={`${MEAL_TYPE_COLORS[recipe.meal_type]} text-white`}>
                {MEAL_TYPE_LABELS[recipe.meal_type] || recipe.meal_type}
              </Badge>
              {recipe.is_ai_generated && (
                <Badge className="bg-purple-500 text-white flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  IA
                </Badge>
              )}
            </div>

            {/* Country flag/label */}
            <div className="absolute bottom-3 left-3">
              <span className="text-white font-medium text-sm bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                {recipe.country}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h3 className="font-heading text-lg font-semibold text-worldplate-text line-clamp-1 group-hover:text-worldplate-primary transition-colors">
              {recipe.name}
            </h3>
            
            <p className="text-worldplate-muted text-sm mt-1 line-clamp-2">
              {recipe.description}
            </p>

            {/* Meta info */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
              <div className="flex items-center gap-3 text-sm text-worldplate-muted">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {totalTime} min
                </span>
                <span className="flex items-center gap-1">
                  <ChefHat className="w-4 h-4" />
                  {recipe.servings} pers.
                </span>
              </div>

              {/* Rating & Difficulty */}
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < recipe.difficulty ? 'text-worldplate-accent fill-worldplate-accent' : 'text-stone-200'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </article>
      </Link>
    </motion.div>
  );
};

export default RecipeCard;
